import { advancedSearchApi } from '@ardoq/api';
import { APIScopeData, ArdoqId } from '@ardoq/api-types';
import {
  ContextSort,
  isArdoqError,
  toByIdDictionary,
} from '@ardoq/common-helpers';
import { EnhancedScopeData, GetCssClassNamesOption } from '@ardoq/data-model';
import { hasFeature } from '@ardoq/features';
import { Locale } from '@ardoq/locale';
import {
  colorsAndRepresentations,
  componentUtils,
  enhancedScopeDataOperations,
  workspaceUtils,
} from '@ardoq/scope-data';
import { NavigatorViewInterface } from '../../NavigatorViewInterface';
import { getNavigatorStubViewInterface } from '../../getNavigatorStubViewInterface';

export const getNavigatorViewInterfaceNoDnD = (
  enhancedScopeData: EnhancedScopeData,
  sortOrder: ContextSort,
  locale: Locale,
  isStrictSelection: boolean = false
): NavigatorViewInterface => {
  return {
    ...getNavigatorStubViewInterface(),
    // Use default implementation for the following functions:
    // The component hierarchy should not apply formatting filters.
    // getCSSFilterColor:
    // isIncludedInContextByFilter:
    // We don't deal with scenarios in the component hierarchy.
    // isScenarioContextComponent:
    // getVisualDiffType:
    // getScenarioName:
    // TODO not sure, but I don't think we need this, styles most likely work
    // without the CSS manager.
    // getComponentCssClassNames:
    // TODO these are writing to the BE (and populating BB collections), we will
    // not deal with them now.
    // copyComponents:
    // isInScenarioMode:
    // getActiveScenarioId:
    // isInDiffMode:
    // getDiffMode:
    // hasWorkspaceWriteAccess:
    // hasComponentWriteAccess:
    // currentUserIsOrgAdmin:
    isIncludedInContextByFilter: () => true,
    isComponentInFlexibleModel: (componentId: ArdoqId) =>
      componentUtils.belongsToFlexibleModel(enhancedScopeData, componentId),
    isFlexibleWorkspace: (workspaceId: ArdoqId) =>
      workspaceUtils.isFlexibleWorkspace(enhancedScopeData, workspaceId),
    canComponentHaveChildren: (componentId: ArdoqId) =>
      componentUtils.canComponentHaveChildren(enhancedScopeData, componentId),
    getComponentLock: (componentId: ArdoqId) =>
      Boolean(
        componentUtils.getAttribute(enhancedScopeData, componentId, 'lock')
      ),
    getComponentTypeId: (componentId: ArdoqId) =>
      componentUtils.getAttribute(enhancedScopeData, componentId, 'typeId'),
    getComponentRepresentationData: (componentId: ArdoqId) =>
      colorsAndRepresentations.getComponentRepresentationData(
        enhancedScopeData,
        componentId
      ),
    getSortedComponentChildrenIds: (componentId: ArdoqId) =>
      componentUtils.getSortedComponentChildrenIds(
        enhancedScopeData,
        componentId,
        sortOrder,
        locale
      ),
    isWorkspaceEmpty: (workspaceId: ArdoqId) =>
      workspaceUtils.isWorkspaceEmpty(enhancedScopeData, workspaceId),
    getWorkspaceRootComponents: (workspaceId: ArdoqId) =>
      workspaceUtils.getRootComponents(enhancedScopeData, workspaceId),
    isWorkspace: (workspaceId: ArdoqId) =>
      workspaceUtils.isWorkspace(enhancedScopeData, workspaceId),
    getRootWorkspaceIdOfComponent: (componentId: ArdoqId) =>
      componentUtils.getWorkspaceId(enhancedScopeData, componentId),
    getComponentName: (componentId: ArdoqId) =>
      componentUtils.getAttribute(enhancedScopeData, componentId, 'name'),
    getComponentOrder: (componentId: ArdoqId) =>
      componentUtils.getAttribute(enhancedScopeData, componentId, '_order'),
    getWorkspaceName: (workspaceId: ArdoqId) =>
      workspaceUtils.getAttribute(enhancedScopeData, workspaceId, 'name'),
    getComponentDisplayColorAsSVGAttributes: (
      componentId: ArdoqId,
      options: GetCssClassNamesOption
    ) =>
      componentUtils.getComponentColorAsSVGAttributes(
        enhancedScopeData,
        componentId,
        options
      ),
    isComponentExternallyManaged: (componentId: ArdoqId) =>
      componentUtils.isExternallyManaged(enhancedScopeData, componentId),
    isComponentExternallyMissing: (componentId: ArdoqId) =>
      componentUtils.isExternallyMissing(enhancedScopeData, componentId),
    isWorkspaceExternallyManaged: (workspaceId: ArdoqId) =>
      workspaceUtils.isExternallyManaged(enhancedScopeData, workspaceId),
    getLinkedSourceComponents: (componentId: ArdoqId) =>
      componentUtils.getSources(enhancedScopeData, componentId),
    getLinkedTargetComponents: (componentId: ArdoqId) =>
      componentUtils.getTargets(enhancedScopeData, componentId),
    hasFeature,
    isComponent: (componentId: ArdoqId) =>
      componentUtils.isComponent(enhancedScopeData, componentId),
    get: (componentId: ArdoqId) =>
      enhancedScopeDataOperations.getComponent(
        enhancedScopeData,
        componentId
      ) ?? null,
    getComponentsData: async (componentIds: ArdoqId[]) => {
      const componentsData = await advancedSearchApi.scopeData({
        componentIds,
      });
      const componentsById = isArdoqError(componentsData)
        ? {}
        : toByIdDictionary(componentsData.components);
      return componentsById;
    },
    getIsStrictSelection: () => isStrictSelection,
    getScopeDataForComponentIds: (componentIds: ArdoqId[]) =>
      getScopeDataForComponentIds(enhancedScopeData, componentIds),
  };
};

const getScopeDataForComponentIds = (
  enhancedScopeData: EnhancedScopeData,
  componentIds: ArdoqId[]
): APIScopeData => {
  const componentIdsSet = new Set([
    ...componentIds,
    ...componentIds.flatMap(id =>
      enhancedScopeDataOperations.getAncestorIds(enhancedScopeData, id)
    ),
  ]);
  const components = enhancedScopeData.components.filter(component =>
    componentIdsSet.has(component._id)
  );
  const workspaceIdsSet = new Set(components.map(c => c.rootWorkspace));
  const workspaces = enhancedScopeData.workspaces.filter(workspace =>
    workspaceIdsSet.has(workspace._id)
  );
  const modelsSet = new Set(workspaces.map(w => w.componentModel));
  const models = enhancedScopeData.models.filter(model =>
    modelsSet.has(model._id)
  );

  const scopeData: APIScopeData = {
    components,
    workspaces,
    models,
    references: [],
    fields: [],
    tags: [],
    scopeComponents: componentIds,
  };
  return scopeData;
};
